import { useCallback, useRef, useState } from "react";

import { useUserpilot } from "@platform/hooks/useUserpilot";
import { useAddBatchTags } from "@components/AddNewEntity/hooks/useAddBatchTags";
import { useCreateCompany } from "@components/AddNewEntity/hooks/useCreateCompany";
import { useUpdateCompany, UpdateCompanyPayload } from "@screens/company/hooks/useUpdateCompany";
import { ICompany } from "@interfaces/company.interface";
import { CreateCompanyRequest } from "@app-types/requests.type";
import { IThemeOption } from "@interfaces/sourcing.interface";
import ToastService from "@services/toast.service";
import { SUCCESS_MESSAGES } from "@config/messages";
import { RelationObjectType } from "@enums/relations.enum";
import { Source } from "@enums/common.enum";
import { arraysEqualUnordered } from "@utils/array.util";
import { openCompanyPage } from "@utils/navigation.util";
import { CreateOrEditCompanyHeader } from "./CreateOrEditCompanyHeader";
import { CompanyForm } from "@components/AddNewEntity/Company";
import { CompanyAvatar, CompanyAvatarSizes } from "@ui/Avatars";
import { H3 } from "@ui/Headings";
import usePlatformStore from "@store/usePlatformStore";

interface ICreateOrEditCompanyProps {
  company?: ICompany;
  currentUrl: string;
  contentType: RelationObjectType;
  hasCompanyLookupError?: boolean;
  onRetryCompanyLookup?: VoidFunction;
}

export default function CreateOrEditCompany({
  company,
  currentUrl,
  contentType,
  hasCompanyLookupError,
  onRetryCompanyLookup,
}: ICreateOrEditCompanyProps) {
  const { track } = useUserpilot();
  const { mutate: addBatchTagsMutate } = useAddBatchTags();
  const { mutateAsync: createCompanyMutate } = useCreateCompany(contentType);
  const { mutate: updateCompanyInline } = useUpdateCompany();
  const [companyFromBrandfetch, setCompanyFromBrandfetch] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isFieldSaveInFlight = useRef(false);
  const pendingFieldSave = useRef<Omit<UpdateCompanyPayload, "id"> | null>(null);
  // Marks the bottom of the panel chrome (header + title row); dropdowns inside
  // CompanyForm use this as their top boundary so they don't render under it.
  const headerRef = useRef<HTMLDivElement>(null);

  const { isOutlook } = usePlatformStore();
  const isExistingCompany = Boolean(company?.id);
  const sourcePlatform = isOutlook() ? Source.OUTLOOK_ADDIN : Source.CHROME_EXTENSION;

  // Inline save: auto-save individual field changes for existing companies.
  // Queues requests so only one PATCH is in-flight at a time — prevents
  // concurrent-update failures when fields change rapidly (e.g. team members).
  const flushFieldSave = useCallback(
    (data: Omit<UpdateCompanyPayload, "id">) => {
      if (!company?.id) return;

      isFieldSaveInFlight.current = true;
      updateCompanyInline(
        { id: company.id, ...data },
        {
          onSettled: () => {
            isFieldSaveInFlight.current = false;
            if (pendingFieldSave.current) {
              const pending = pendingFieldSave.current;
              pendingFieldSave.current = null;
              flushFieldSave(pending);
            }
          },
        }
      );
    },
    [company?.id, updateCompanyInline]
  );

  const handleFieldSave = useCallback(
    (data: Omit<UpdateCompanyPayload, "id">) => {
      if (!company?.id) return;

      // Skip update if the value hasn't changed
      const currentValues: Record<string, unknown> = {
        priority: company.priority?.id || null,
        type: company.type?.id || null,
        stage_object_id: company.stage_object_id || null,
        content_type: company.active_deal ? "deals" : "companies",
        platform_company: company.platform_company?.id || null,
        title: company.title,
        users: company.users ?? [],
      };

      const hasChanges = Object.keys(data).some((key) => {
        const newVal = data[key as keyof CreateCompanyRequest];
        const curVal = currentValues[key];

        if (key === "users" && Array.isArray(newVal) && Array.isArray(curVal)) {
          return !arraysEqualUnordered(newVal as string[], curVal as string[]);
        }

        return newVal !== curVal;
      });

      if (!hasChanges) return;

      if (isFieldSaveInFlight.current) {
        pendingFieldSave.current = {
          ...pendingFieldSave.current,
          ...data,
          hasSharedAttributeUpdates:
            pendingFieldSave.current?.hasSharedAttributeUpdates || data.hasSharedAttributeUpdates,
        };
        return;
      }

      flushFieldSave(data);
    },
    [company, flushFieldSave]
  );

  // Inline save: auto-save theme additions for existing companies
  const handleThemeAdd = useCallback(
    (theme: IThemeOption, removedParentIds: string[]) => {
      if (!company?.id) return;
      addBatchTagsMutate(
        {
          relations_to_create: [
            {
              left_content_type: RelationObjectType.COMPANY,
              left_object_id: company.id,
              right_content_type: RelationObjectType.THEME,
              right_object_id: theme.id,
              relation_type: null,
            },
          ],
          relations_to_delete: [],
          pairs_to_delete: removedParentIds.map((parentId) => ({
            left_content_type: RelationObjectType.COMPANY,
            left_object_id: company.id,
            right_content_type: RelationObjectType.THEME,
            right_object_id: parentId,
          })),
        },
        {
          onSuccess: () => {
            ToastService.showSuccess(SUCCESS_MESSAGES.COMPANY_UPDATED(company.title));
          },
        }
      );
    },
    [company?.id, company?.title, addBatchTagsMutate]
  );

  // Inline save: auto-save theme removals for existing companies
  const handleThemeRemove = useCallback(
    (theme: IThemeOption, addedParentId: string | null) => {
      if (!company?.id) return;
      addBatchTagsMutate(
        {
          relations_to_create: addedParentId
            ? [
                {
                  left_content_type: RelationObjectType.COMPANY,
                  left_object_id: company.id,
                  right_content_type: RelationObjectType.THEME,
                  right_object_id: addedParentId,
                  relation_type: null,
                },
              ]
            : [],
          relations_to_delete: [],
          pairs_to_delete: [
            {
              left_content_type: RelationObjectType.COMPANY,
              left_object_id: company.id,
              right_content_type: RelationObjectType.THEME,
              right_object_id: theme.id,
            },
          ],
        },
        {
          onSuccess: () => {
            ToastService.showSuccess(SUCCESS_MESSAGES.COMPANY_UPDATED(company.title));
          },
        }
      );
    },
    [company?.id, company?.title, addBatchTagsMutate]
  );

  // Batch save: used only for creating new companies
  const handleCreateCompany = async (data: CreateCompanyRequest) => {
    setIsSubmitting(true);

    try {
      await createCompanyMutate(data);
      track("Click: Create company submit (external website)");
      return true;
    } catch {
      // useCreateCompany shows a toaster message so we just suppress the error here
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const companyTitle = company?.title || companyFromBrandfetch?.name || "New Company";

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <CreateOrEditCompanyHeader
        isExistingCompany={isExistingCompany}
        hasError={hasCompanyLookupError}
        onRetry={onRetryCompanyLookup}
        onClick={() => company?.id && openCompanyPage(company.id)}
      />

      {/* Title */}
      <div
        ref={headerRef}
        className="flex gap-2 items-center px-4 mt-2 mb-4"
        onClick={() => company?.id && openCompanyPage(company.id)}
      >
        <CompanyAvatar
          size={CompanyAvatarSizes.SMALL}
          classNames="rounded-[5px] w-5 h-5 border border-gray-200 bg-background-clear flex-shrink-0 !cursor-pointer"
          logoUrl={company?.logo_url || companyFromBrandfetch?.logo}
        />
        <H3 className="truncate text-overflow-ellipsis text-left !cursor-pointer">
          {companyTitle}
        </H3>
      </div>

      {/* Form */}
      <CompanyForm
        key={company?.id ?? currentUrl}
        containerClassName="h-[calc(100vh_-_116px)] w-full flex flex-col justify-between"
        company={company}
        initialWebsite={currentUrl}
        sourcePlatform={sourcePlatform}
        onSubmit={handleCreateCompany}
        onFieldSave={isExistingCompany ? handleFieldSave : undefined}
        onThemeAdd={isExistingCompany ? handleThemeAdd : undefined}
        onThemeRemove={isExistingCompany ? handleThemeRemove : undefined}
        onUpdateCompanyFromBrandfetch={setCompanyFromBrandfetch}
        hideSecondaryButton={!isExistingCompany}
        isSubmitting={isSubmitting}
        headerRef={headerRef}
        showAttributes
      />
    </div>
  );
}
